import { useState, useCallback } from "react";
import axios from "axios";

const FALLBACK_LOCATION = {
  provinsi: "DKI Jakarta",
  kabkota: "Kota Jakarta Pusat",
};

/**
 * Mapping nama provinsi dari Nominatim (OpenStreetMap)
 * ke nama yang dikenali oleh API equran.id (Kemenag RI).
 */
const mapProvinceName = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.includes("jakarta")) return "DKI Jakarta";
  if (lower.includes("yogyakarta")) return "DI Yogyakarta";
  if (lower.includes("jawa barat") || lower.includes("west java")) return "Jawa Barat";
  if (lower.includes("jawa tengah") || lower.includes("central java")) return "Jawa Tengah";
  if (lower.includes("jawa timur") || lower.includes("east java")) return "Jawa Timur";
  if (lower.includes("sumatera utara") || lower.includes("north sumatra")) return "Sumatera Utara";
  if (lower.includes("sumatera barat") || lower.includes("west sumatra")) return "Sumatera Barat";
  if (lower.includes("sumatera selatan") || lower.includes("south sumatra")) return "Sumatera Selatan";
  if (lower.includes("sulawesi selatan") || lower.includes("south sulawesi")) return "Sulawesi Selatan";
  if (lower.includes("sulawesi utara") || lower.includes("north sulawesi")) return "Sulawesi Utara";
  if (lower.includes("kalimantan timur") || lower.includes("east kalimantan")) return "Kalimantan Timur";
  if (lower.includes("kalimantan barat") || lower.includes("west kalimantan")) return "Kalimantan Barat";
  if (lower.includes("nusa tenggara barat") || lower.includes("west nusa tenggara")) return "Nusa Tenggara Barat";
  if (lower.includes("nusa tenggara timur") || lower.includes("east nusa tenggara")) return "Nusa Tenggara Timur";
  return name;
};

/**
 * Mencari item dalam daftar yang paling cocok dengan target string.
 */
const findBestMatch = (list, target) => {
  if (!target || !list.length) return null;
  const normalizedTarget = mapProvinceName(target).toLowerCase();
  return list.find((item) => {
    const normalizedItem = item.toLowerCase();
    return (
      normalizedTarget.includes(normalizedItem) ||
      normalizedItem.includes(normalizedTarget)
    );
  });
};

/**
 * Custom hook untuk deteksi lokasi GPS + reverse geocoding.
 *
 * Mengembalikan:
 * - detecting: boolean (apakah sedang mendeteksi)
 * - error: string | null (pesan error jika gagal)
 * - detect: function(provinces, onSuccess, onError)
 *     onSuccess({ provinsi, kabkota, allCities }) → dipanggil jika berhasil
 *     onError(fallbackLocation) → dipanggil jika gagal, fallback ke Jakarta
 */
export default function useGeolocation() {
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Mendeteksi lokasi pengguna via GPS browser,
   * lalu mencari provinsi & kota yang cocok dari API Kemenag.
   *
   * @param {string[]} provinces - Daftar nama provinsi dari API
   * @param {function} fetchCities - Async function(provinsi) → string[]
   * @param {function} onSuccess - Callback({ provinsi, kabkota }) saat berhasil
   * @param {function} onFallback - Callback({ provinsi, kabkota }) saat fallback
   */
  const detect = useCallback(
    async (provinces, fetchCities, onSuccess, onFallback) => {
      if (!navigator.geolocation) {
        setError("Geolocation tidak didukung oleh browser Anda.");
        onFallback(FALLBACK_LOCATION);
        return;
      }

      setDetecting(true);
      setError(null);

      // Timeout manual 10 detik — fallback ke Jakarta jika GPS lambat
      const geoTimeout = setTimeout(async () => {
        setDetecting(false);
        const cities = await fetchCities(FALLBACK_LOCATION.provinsi);
        onFallback({ ...FALLBACK_LOCATION, cities });
      }, 10000);

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          clearTimeout(geoTimeout);
          try {
            const res = await axios.get(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`
            );
            const addr = res.data.address;
            const cityName = addr.city || addr.town || addr.city_district || addr.county;
            const stateName = addr.state;

            const matchedProv = findBestMatch(provinces, stateName);
            if (matchedProv) {
              const cities = await fetchCities(matchedProv);
              const matchedCity = cities.find(
                (c) =>
                  cityName.toLowerCase().includes(c.toLowerCase()) ||
                  c.toLowerCase().includes(cityName.toLowerCase())
              );

              if (matchedCity) {
                onSuccess({ provinsi: matchedProv, kabkota: matchedCity });
              } else {
                setError(`Berada di ${stateName}, silakan pilih kota.`);
                onSuccess({ provinsi: matchedProv, kabkota: "Pilih Kab/Kota" });
              }
              return;
            }
            throw new Error("No location match found");
          } catch {
            const cities = await fetchCities(FALLBACK_LOCATION.provinsi);
            onFallback({ ...FALLBACK_LOCATION, cities });
          } finally {
            setDetecting(false);
          }
        },
        (err) => {
          clearTimeout(geoTimeout);
          setDetecting(false);
          const msg =
            err.code === 1
              ? "Izin lokasi ditolak."
              : "Gagal mendapat lokasi.";
          setError(msg);
          onFallback(FALLBACK_LOCATION);
        },
        { timeout: 10000 }
      );
    },
    []
  );

  return { detecting, error, detect, setError };
}

export { mapProvinceName, findBestMatch, FALLBACK_LOCATION };
